<template>
  <v-container fluid class="overflow-y-auto">
    <v-row class="ml-0">
      <v-col cols="10" class="titleLarge d-flex align-center">
        <p class="opacity87">{{ $t('views.incidentPanel.details.title') }}</p>
      </v-col>
      <v-col cols="12" class="pt-0 px-0 pb-0">
        <DetailsTexts :value="incident.incident_number" :label="$t('domain.fields.incidentNumber')" />
      </v-col>
      <v-col cols="12" class="px-0 py-0 mt-2">
        <DetailsTexts :value="getDate" :label="$t('domain.fields.reportedDateTime')" />
      </v-col>
      <v-col cols="12" class="px-0 py-0 mt-2">
        <DetailsTexts :value="containmentMeassurement!" :label="$t('domain.fields.percentContained')" suffix="%" />
      </v-col>
      <v-col cols="12" class="px-0 py-0 mt-2">
        <DetailsTexts :value="sizeMeasurement!" :label="$t('domain.fields.incidentSize')" suffix="ha" />
      </v-col>
      <v-col cols="12" class="px-0 py-0 mt-2">
        <DetailsTexts :value="$t(`domain.enums.incidentCategory.${incident.category.i18NKey}`)" :label="$t(`domain.fields.category`)" />
      </v-col>

      <v-col cols="12" class="px-0 py-0 mt-2">
        <DetailsTexts :value="complexityTraduction" :label="$t('domain.fields.complexity')" />
      </v-col>
      <v-col cols="12" class="px-0 py-0 mt-2">
        <DetailsTexts :value="displayModeTraduction" :label="$t('domain.fields.displayMode')" />
      </v-col>

      <v-col cols="10" class="px-0 py-0 mt-2">
        <label class="bodyTextsSmall opacity67">{{ $t('domain.fields.originLocation') }}</label>
        <p class="titleIncident opacity87">
          {{ incident?.latitude?.toFixed(4) }} ,
          {{ incident?.longitude?.toFixed(4) }}
        </p>
      </v-col>
      <v-col cols="2" class="px-0 py-0 mt-2 text-end">
        <v-btn icon flat @click="copyIncident()">
          <v-icon>mdi-content-copy</v-icon>
          <v-tooltip activator="parent" location="bottom">{{ $t('common.actions.copy') }}</v-tooltip>
        </v-btn>
      </v-col>
      <v-col cols="12" class="px-0 py-0 mt-2">
        <DetailsTexts :value="adminDivisionLevel1 ?? ''" :label="$t('domain.fields.adminDivisionLevel1')" />
      </v-col>
      <v-col cols="12" class="px-0 py-0 mt-2">
        <DetailsTexts :value="adminDivisionLevel2 ?? ''" :label="$t('domain.fields.adminDivisionLevel2')" />
      </v-col>
      <v-col cols="12" class="px-0 py-0 mt-2">
        <DetailsTexts :value="'-'" :label="$t('domain.fields.respondingAgencies')" />
      </v-col>
    </v-row>

    <v-btn
      v-if="authStore.hasPermission('incident-edit') && authStore.user && incidentStore.isFromUserOrganization(authStore.user, incident)"
      icon
      color="rgb(var(--v-theme-primary))"
      size="large"
      class="absolute"
      @click="emits('openEditDetailsEvent')">
      <v-icon>mdi-pencil</v-icon>
      <v-tooltip activator="parent" location="bottom">{{ $t('common.actions.edit') }}</v-tooltip>
    </v-btn>
  </v-container>
</template>

<script setup lang="ts">
// Store import
import SettingsStore from '@/stores/settingsStore.ts'
import IncidentStore from '@/stores/incidentStore'

const settings = SettingsStore()
const authStore = AuthStore()
const incidentStore = IncidentStore()

// Frameworks Import
import { computed, toRef } from 'vue'

import { useClipboard } from '@vueuse/core'
import { useI18n } from 'vue-i18n'

// Types import

// Component Import

// Zapan Imports
import { useNotifications } from '@tsyl/zapan2'

import getMeassurementData from '@/composables/getMeassurementData'
import { useAdminDivisions } from '@/composables/useAdminDivisions'
import AuthStore from '@/stores/auth'
import type { Incident } from '@/types/Incident'

import DetailsTexts from './DetailsTexts.vue'

const { addNotification } = useNotifications()

const props = defineProps<{ incident: Incident }>()

const emits = defineEmits<{
  (event: 'openEditDetailsEvent'): void
}>()

const { t } = useI18n()

const incident = toRef(props, 'incident')

const { level1: adminDivisionLevel1, level2: adminDivisionLevel2 } = useAdminDivisions(() => incident.value)

const getDate = computed(() => {
  return settings.formatStringDateTime(incident.value.declared_timestamp)
})

const sizeMeasurement = getMeassurementData(incident, 'Size')

const containmentMeassurement = getMeassurementData(incident, 'Containment')

function copyIncident() {
  addNotification({
    type: 'success',
    title: t('domain.fields.coordinates').concat(' ', t('messages.success.copied')),
    time: 2000
  })

  const { copy } = useClipboard({
    source: ` ${props.incident.latitude?.toFixed(4)} , ${props.incident.longitude?.toFixed(4)}`
  })
  return copy()
}

const complexityTraduction = computed(() => {
  const complexityKey = incident.value.complexity?.name ?? 'none'
  return t(`domain.enums.complexity.${complexityKey.toLowerCase()}`)
})
const displayModeTraduction = computed(() => {
  const displayModeKey = incident.value.displayMode?.name ?? 'private'
  return t(`domain.enums.displayMode.${displayModeKey.toLowerCase()}`)
})
</script>

<style scoped lang="scss">
.absolute {
  position: absolute;
  bottom: 31px;
  right: 27px;
}

.details {
  overflow-y: auto;
  height: 100%;
}
</style>
